import {
  ICourse,
  ProgressStatus,
  stripCourseThumbnailBase64,
  teacherTrainingDB,
  TrainingCourseProgressInfo,
  TrainingStatusRecord,
  UserRole,
  userDB,
} from '@taleemabad/db';

const normalizeProfileId = (id: string | null | undefined): string =>
  (id ?? '').replace(/[-_]/g, '').toLowerCase();

const profileIdsMatch = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => normalizeProfileId(a) === normalizeProfileId(b);

class TeacherTrainingService {
  async getNextTrainingAndCourse(): Promise<TrainingCourseProgressInfo | null> {
    return this.getTrainingForExistingUser();
  }

  private async getCourseById(
    courseId: number,
    profileId: string | null,
  ): Promise<ICourse | undefined> {
    if (profileId) {
      return teacherTrainingDB.coursesV2
        .filter(
          (r) => r.id === courseId && profileIdsMatch(r.profileId, profileId),
        )
        .first();
    }
    return teacherTrainingDB.coursesV2.filter((r) => r.id === courseId).first();
  }

  private async getActiveProfileId(): Promise<string | null> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles.find(
      (p) => p.type === UserRole.Teacher || p.type === UserRole.Coach,
    );
    return profile?.participantId ?? null;
  }

  private async getTrainingForExistingUser(): Promise<TrainingCourseProgressInfo | null> {
    const profileId = await this.getActiveProfileId();
    const totalLevels = 4;
    // Filter by profile at the DB level and strip thumbnailBase64 blobs.
    // The unfiltered .toArray() loaded ALL profiles' courses with full base64
    // thumbnails (~150-300KB each), pushing V8 heap to OOM on large schools.
    const allCourses = (
      await teacherTrainingDB.coursesV2
        .filter(
          (c) =>
            c.syncStatus !== 'deleted' &&
            (profileId === null || profileIdsMatch(c.profileId, profileId)),
        )
        .toArray()
    ).map(stripCourseThumbnailBase64);

    for (let i = 0; i <= totalLevels; i++) {
      const coursesForLevel = allCourses
        .filter((c) => c.level?.value === i)
        .sort((a, b) => a.index - b.index);

      for (const course of coursesForLevel) {
        if (course.courseCompletionPercentage !== 100) {
          const trainings = (
            await teacherTrainingDB.trainingsV2
              .filter(
                (r) =>
                  r.course === course.id &&
                  (profileId === null ||
                    profileIdsMatch(r.profileId, profileId)),
              )
              .toArray()
          ).sort((a, b) => a.index - b.index);
          const inProgressTraining = trainings.find(
            (training) => training.trainingStatus !== ProgressStatus.Completed,
          );

          if (inProgressTraining) {
            return {
              course,
              training: inProgressTraining,
              cta: 'Resume Training',
              progressText: 'Lets continue where you left!',
              allTrainingsCompleted: false,
            };
          }
        }
      }
    }
    return null;
  }

  private async nextTrainingForCompletedTraining(
    latestTrainingStatus: TrainingStatusRecord,
  ): Promise<TrainingCourseProgressInfo | null> {
    // go through all the trainings in currentCourse and get the next training
    const profileId = await this.getActiveProfileId();
    const currentCourse = profileId
      ? await teacherTrainingDB.coursesV2
          .filter(
            (r) =>
              r.id === latestTrainingStatus.courseId &&
              profileIdsMatch(r.profileId, profileId),
          )
          .first()
      : await teacherTrainingDB.coursesV2
          .filter((r) => r.id === latestTrainingStatus.courseId)
          .first();

    if (!currentCourse) {
      return null;
    }

    const allTrainingsForCurrentCourse = (
      await teacherTrainingDB.trainingsV2
        .filter(
          (r) =>
            r.course === currentCourse.id &&
            (profileId === null || profileIdsMatch(r.profileId, profileId)),
        )
        .toArray()
    ).sort((a, b) => a.index - b.index);

    const nextTraining = allTrainingsForCurrentCourse.find(
      (t) => t.trainingStatus !== ProgressStatus.Completed,
    );
    if (nextTraining) {
      currentCourse.courseCompletionPercentage =
        await this.getCourseCompletionPercentage(currentCourse.id);
      return {
        course: currentCourse,
        training: nextTraining,
        cta: 'Resume Training',
        progressText: 'Let\u2019s continue where you left!',
        allTrainingsCompleted: false,
      };
    }
    // now there might be cases where all the trainings are completed for currentCourse, in that case we return the next training in next course
    return this.getFirstIncompleteTrainingAndCourse(latestTrainingStatus);
  }

  private async getFirstIncompleteTrainingAndCourse(
    latestTrainingStatus: TrainingStatusRecord,
  ): Promise<TrainingCourseProgressInfo | null> {
    // if the course which was previously completed a general course, then we need to get the next training in the next general course
    // here we divide the courses into five groups, on the basis of course type
    const profileId = await this.getActiveProfileId();
    const currentCourse = await this.getCourseById(
      latestTrainingStatus.courseId,
      profileId,
    );

    if (!currentCourse?.type) {
      return {
        course: null,
        training: null,
        cta: null,
        progressText: null,
        allTrainingsCompleted: true,
      };
    }

    const allCoursesRaw = (
      await teacherTrainingDB.coursesV2
        .filter(
          (c) =>
            c.syncStatus !== 'deleted' &&
            (profileId === null || profileIdsMatch(c.profileId, profileId)),
        )
        .sortBy('index')
    ).map(stripCourseThumbnailBase64);

    const allCourses = allCoursesRaw;

    // Categorizing courses in a single pass
    const courseGroupsMap: Record<string, ICourse[]> = {
      GENERAL: [],
      SUBJECT: [],
      FLN: [],
      CHARACTER_EDUCATION: [],
      EARLY_YEARS: [],
      PEDAGOGICAL_PRACTICE: [],
      CONTENT_EXPERTISE: [],
      ASSESSMENT_FEEDBACK: [],
      INCLUSIVE_EDUCATION: [],
      DIGITAL_LITERACY: [],
      CLASSROOM_MANAGEMENT: [],
      PROFESSIONAL_GROWTH_ETHICS: [],
    };

    for (const course of allCourses) {
      if (courseGroupsMap[course.type]) {
        courseGroupsMap[course.type].push(course);
      }
    }

    // Define the sequence of course groups
    const courseGroupOrder: Record<string, string[]> = {
      GENERAL: [
        'SUBJECT',
        'GENERAL',
        'FLN',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      SUBJECT: [
        'EARLY_YEARS',
        'SUBJECT',
        'GENERAL',
        'FLN',
        'CHARACTER_EDUCATION',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      FLN: [
        'GENERAL',
        'FLN',
        'SUBJECT',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      CHARACTER_EDUCATION: [
        'GENERAL',
        'CHARACTER_EDUCATION',
        'SUBJECT',
        'FLN',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      EARLY_YEARS: [
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'GENERAL',
        'SUBJECT',
        'FLN',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      PEDAGOGICAL_PRACTICE: [
        'GENERAL',
        'PEDAGOGICAL_PRACTICE',
        'SUBJECT',
        'FLN',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
      ],
      CONTENT_EXPERTISE: [
        'GENERAL',
        'CONTENT_EXPERTISE',
        'SUBJECT',
        'FLN',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      ASSESSMENT_FEEDBACK: [
        'GENERAL',
        'ASSESSMENT_FEEDBACK',
        'SUBJECT',
        'FLN',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      INCLUSIVE_EDUCATION: [
        'GENERAL',
        'INCLUSIVE_EDUCATION',
        'SUBJECT',
        'FLN',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      DIGITAL_LITERACY: [
        'GENERAL',
        'DIGITAL_LITERACY',
        'SUBJECT',
        'FLN',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'CLASSROOM_MANAGEMENT',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      CLASSROOM_MANAGEMENT: [
        'GENERAL',
        'CLASSROOM_MANAGEMENT',
        'SUBJECT',
        'FLN',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'PROFESSIONAL_GROWTH_ETHICS',
      ],
      PROFESSIONAL_GROWTH_ETHICS: [
        'GENERAL',
        'PROFESSIONAL_GROWTH_ETHICS',
        'SUBJECT',
        'FLN',
        'CHARACTER_EDUCATION',
        'EARLY_YEARS',
        'PEDAGOGICAL_PRACTICE',
        'CONTENT_EXPERTISE ',
        'ASSESSMENT_FEEDBACK',
        'INCLUSIVE_EDUCATION',
        'DIGITAL_LITERACY',
        'CLASSROOM_MANAGEMENT',
      ],
    };

    const courseGroupsToCheck =
      courseGroupOrder[currentCourse.type] || Object.keys(courseGroupsMap);

    const orderedCourseGroups = courseGroupsToCheck
      .map((group) => courseGroupsMap[group])
      .filter((group) => group && group.length > 0);

    for (const courseGroup of orderedCourseGroups) {
      const result = await this.findIncompleteTrainingInGroup(
        courseGroup,
        profileId,
      );
      if (result) {
        return result;
      }
    }

    // All trainings are completed
    return {
      course: null,
      training: null,
      cta: null,
      progressText: null,
      allTrainingsCompleted: true,
    };
  }

  private async findIncompleteTrainingInGroup(
    courseGroup: ICourse[],
    profileId: string | null,
  ): Promise<TrainingCourseProgressInfo | null> {
    for (const course of courseGroup) {
      const allTrainingsInCourse = (
        await teacherTrainingDB.trainingsV2
          .filter(
            (r) =>
              r.course === course.id &&
              (profileId === null || profileIdsMatch(r.profileId, profileId)),
          )
          .toArray()
      ).sort((a, b) => a.index - b.index);

      for (const training of allTrainingsInCourse) {
        if (training.trainingStatus !== ProgressStatus.Completed) {
          course.courseCompletionPercentage =
            await this.getCourseCompletionPercentage(course.id);
          return {
            course,
            training,
            cta: 'Resume Training',
            progressText: 'Lets continue where you left!',
            allTrainingsCompleted: false,
          };
        }
      }
    }
    return null;
  }

  async getCourseCompletionPercentage(courseId: number): Promise<number> {
    const profileId = await this.getActiveProfileId();
    const trainings = await teacherTrainingDB.trainingsV2
      .filter(
        (r) =>
          r.course === courseId &&
          (profileId === null || profileIdsMatch(r.profileId, profileId)),
      )
      .toArray();
    const completedTrainings = trainings.filter(
      (t) => t.trainingStatus === ProgressStatus.Completed,
    ).length;
    const percentage =
      trainings.length > 0 ? (completedTrainings / trainings.length) * 100 : 0;
    return Math.round(percentage);
  }
}

export const teacherTrainingService = new TeacherTrainingService();
